import { Phone } from 'lucide-react';

/**
 * Click-to-call CTA, the dominant primary action across the directory.
 * Renders as an <a href="tel:..."> styled like a Btn.
 *
 *   <PhoneCTA number="(213) 555-0148" />
 *   <PhoneCTA number="1-833-INJURY-1" size="xl" full />
 */

export type PhoneCTAKind = 'primary' | 'secondary' | 'ghost';
export type PhoneCTASize = 'sm' | 'md' | 'lg' | 'xl';

export interface PhoneCTAProps {
    number: string;
    kind?: PhoneCTAKind;
    size?: PhoneCTASize;
    full?: boolean;
    label?: string;
    className?: string;
}

const KIND_CLASSES: Record<PhoneCTAKind, string> = {
    primary: 'bg-ember text-paper hover:bg-ember-deep',
    secondary: 'bg-card text-ink border border-rule hover:border-ink-soft',
    ghost: 'bg-transparent text-ink hover:bg-paper-deep',
};

const SIZE_CLASSES: Record<PhoneCTASize, string> = {
    sm: 'h-9 px-3.5 text-[14px] gap-1.5',
    md: 'h-11 px-4.5 text-[15px] gap-2',
    lg: 'h-13 px-5.5 text-[16px] gap-2',
    xl: 'h-15 px-7 text-[17px] gap-2.5',
};

function telHref(number: string): string {
    return 'tel:' + number.replace(/[^0-9+]/g, '');
}

export function PhoneCTA({
    number,
    kind = 'primary',
    size = 'lg',
    full = false,
    label,
    className,
}: PhoneCTAProps) {
    const cls = [
        'inline-flex items-center justify-center font-medium tracking-tight transition-colors',
        'rounded-md no-underline',
        SIZE_CLASSES[size],
        KIND_CLASSES[kind],
        full ? 'w-full' : '',
        className ?? '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <a href={telHref(number)} aria-label={`Call ${number}`} className={cls}>
            <Phone size={16} aria-hidden="true" />
            {label ?? number}
        </a>
    );
}

export default PhoneCTA;
